import React, { useState, useEffect } from 'react';
import { apiPost, ApiError, zeigeApiFehler } from '../../lib/api';
import { toast } from '../../lib/toast';
import { globalNav } from '../../lib/nav';
import { LdSheet } from '../../components/ui/LdSheet';

interface PreviewSlot { reihenfolge: number; datum: string; start_zeit: string; end_zeit: string; }
interface Preview {
  schueler: { id: number; vorname: string; nachname: string; klasse_name: string | null };
  eltern_email: string | null;
  typ: 'vor_ort' | 'telefonisch';
  default_typ: 'vor_ort' | 'telefonisch';
  slots: PreviewSlot[];
}

function fmtDatum(d: string): string {
  return new Date(d + 'T12:00:00').toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
}

function baueBody(p: Preview, typ: 'vor_ort' | 'telefonisch', sel: PreviewSlot[]): string {
  const typText = typ === 'telefonisch'
    ? 'Folgende Telefon-Termine biete ich Ihnen an:'
    : 'Folgende Zeiten würden mir vor Ort passen:';
  const zeilen = sel.map(s => `• ${fmtDatum(s.datum)} um ${s.start_zeit} Uhr`).join('\n');
  return `Sehr geehrte Erziehungsberechtigte,

gerne möchte ich mit Ihnen über ${p.schueler.vorname} ${p.schueler.nachname} sprechen.

${typText}

${zeilen}

Bitte teilen Sie mir mit, welcher Termin Ihnen passt.

Mit freundlichen Grüßen`;
}

export function TerminVorschlagenModal({ open, mailId = null, schuelerId = null, onClose }: { open: boolean; mailId?: number | null; schuelerId?: number | null; onClose: () => void }) {
  const [laden, setLaden]     = useState(true);
  const [fehler, setFehler]   = useState<string | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [typ, setTyp]         = useState<'vor_ort' | 'telefonisch'>('vor_ort');
  const [sel, setSel]         = useState<Set<number>>(new Set());
  const [body, setBody]       = useState('');
  const [manuell, setManuell] = useState(false);
  const [senden, setSenden]   = useState(false);
  const [drafts, setDrafts]   = useState<{ to: string; subject: string; body: string } | null>(null);

  useEffect(() => {
    if (!open || (mailId == null && schuelerId == null)) return;
    setLaden(true); setFehler(null); setPreview(null); setManuell(false); setDrafts(null);
    apiPost<any>('/api/terminvorschlaege/generieren',
      { mail_id: mailId ?? undefined, schueler_id: schuelerId ?? undefined, mode: 'preview' })
      .then(d => {
        setPreview(d);
        setTyp(d.typ ?? d.default_typ ?? 'vor_ort');
        setSel(new Set(d.slots.map((s: PreviewSlot) => s.reihenfolge)));
      })
      // Der Fehler-Code steuert die UI (z.B. 'keine_eltern_email' → Hinweistext)
      .catch(e => setFehler(e instanceof ApiError ? ((e.body as any)?.fehler ?? 'fehler') : 'fehler'))
      .finally(() => setLaden(false));
  }, [open, mailId, schuelerId]);

  // Mail-Body auto-generieren (außer manuell editiert)
  useEffect(() => {
    if (!preview || manuell) return;
    const selSlots = preview.slots.filter(s => sel.has(s.reihenfolge));
    setBody(baueBody(preview, typ, selSlots));
  }, [preview, typ, sel, manuell]);

  if (!open) return null;

  const selSlots = preview ? preview.slots.filter(s => sel.has(s.reihenfolge)) : [];
  const subject = preview ? `${mailId != null ? 'Termin-Vorschlag' : 'Termin-Anfrage'}: ${preview.schueler.vorname} ${preview.schueler.nachname}` : '';

  async function absenden() {
    if (!preview || selSlots.length === 0) { toast('Bitte mindestens einen Termin wählen.', 'error'); return; }
    setSenden(true);
    try {
      const d = await apiPost<any>('/api/terminvorschlaege/persistieren-und-senden', {
        schueler_id: preview.schueler.id, mail_id: mailId ?? undefined, typ,
        slots: selSlots.map(s => ({ datum: s.datum, start_zeit: s.start_zeit, end_zeit: s.end_zeit })),
        mail_draft: { to: preview.eltern_email ?? '', subject, body },
      });
      if (d.gesendet) { toast('Termin-Vorschlag gesendet', 'success'); onClose(); }
      else { setDrafts(d.mail_draft_zum_kopieren ?? { to: preview.eltern_email ?? '', subject, body }); }
    } catch (e) { zeigeApiFehler(e, 'Senden fehlgeschlagen'); }
    finally { setSenden(false); }
  }

  const inp: React.CSSProperties = { width: '100%', padding: '10px 12px', borderRadius: 10, border: '1.5px solid var(--ld-outline-soft)', fontSize: 14, outline: 'none', boxSizing: 'border-box', background: 'var(--ld-bg-input)', color: 'var(--ld-text-primary)' };

  return (
    <LdSheet open={open} onClose={onClose} maxWidth={540} title="Termin vorschlagen" ariaLabel="Termin vorschlagen">
      <div style={{ padding: '0 20px 28px' }}>

        {laden ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--ld-text-secondary)', fontSize: 14 }}>Suche freie Termine …</div>
        ) : fehler ? (
          <div style={{ padding: '8px 0 16px' }}>
            {fehler === 'sprechzeit_fehlt' ? (
              <div>
                <p style={{ fontSize: 14, color: 'var(--ld-text-primary)', margin: '0 0 12px' }}>Du hast noch keine Eltern-Sprechzeit konfiguriert.</p>
                <button onClick={() => { onClose(); globalNav('einstellungen'); }} style={{ padding: '10px 16px', borderRadius: 10, border: 'none', background: 'var(--ld-primary)', color: 'white', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Zu den Einstellungen →</button>
              </div>
            ) : fehler === 'keine_slots_verfuegbar' ? (
              <p style={{ fontSize: 14, color: 'var(--ld-text-secondary)', margin: 0 }}>Keine freien Termine in den nächsten 14 Tagen. Bitte Sprechzeit-Fenster erweitern.</p>
            ) : fehler === 'kein_schueler' ? (
              <p style={{ fontSize: 14, color: 'var(--ld-text-secondary)', margin: 0 }}>Diese Mail ist keinem Schüler zugeordnet. Lege zuerst in der Klasse eine Schülerin oder einen Schüler an.</p>
            ) : (
              <p style={{ fontSize: 14, color: 'var(--ld-text-secondary)', margin: 0 }}>Termine konnten nicht geladen werden.</p>
            )}
          </div>
        ) : drafts ? (
          <div>
            <p style={{ fontSize: 13, color: 'var(--ld-text-secondary)', margin: '0 0 12px' }}>Gmail nicht verbunden — Vorschlag wurde gespeichert. Bitte Text manuell senden:</p>
            <div style={{ fontSize: 12, color: 'var(--ld-text-secondary)', marginBottom: 6 }}>An: {drafts.to || '—'} · Betreff: {drafts.subject}</div>
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, background: 'var(--ld-bg-input)', borderRadius: 8, padding: '10px 12px', fontFamily: 'inherit', maxHeight: 200, overflowY: 'auto' }}>{drafts.body}</pre>
            <button onClick={() => { navigator.clipboard?.writeText(`${drafts.subject}\n\n${drafts.body}`).then(() => toast('Kopiert', 'success'), () => {}); }} style={{ marginTop: 10, padding: '8px 14px', borderRadius: 10, border: 'none', background: 'var(--ld-primary)', color: 'white', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Text kopieren</button>
            <button onClick={onClose} style={{ marginTop: 10, marginLeft: 8, padding: '8px 14px', borderRadius: 10, border: '1.5px solid var(--ld-outline-soft)', background: 'var(--ld-bg-input)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Fertig</button>
          </div>
        ) : preview ? (
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ld-text-primary)', marginBottom: 4 }}>
              {preview.schueler.vorname} {preview.schueler.nachname}{preview.schueler.klasse_name ? ` (${preview.schueler.klasse_name})` : ''}
            </div>
            <div style={{ fontSize: 12, color: preview.eltern_email ? 'var(--ld-text-secondary)' : '#DC2626', marginBottom: 14 }}>
              {preview.eltern_email ? `An: ${preview.eltern_email}` : 'Kein Eltern-Hauptkontakt hinterlegt'}
            </div>

            {/* Typ */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              {([['vor_ort', 'Vor Ort'], ['telefonisch', 'Telefonisch']] as const).map(([v, l]) => (
                <button key={v} onClick={() => setTyp(v)} style={{ flex: 1, padding: '8px 0', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, border: typ === v ? '1.5px solid var(--ld-primary)' : '1.5px solid var(--ld-outline-soft)', background: typ === v ? 'var(--ld-primary-light, #EDE9F8)' : 'var(--ld-bg-input)', color: typ === v ? 'var(--ld-primary)' : 'var(--ld-text-secondary)' }}>{l}</button>
              ))}
            </div>

            {/* Slots */}
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ld-text-secondary)', marginBottom: 6 }}>Verfügbare Termine</div>
            <div style={{ border: '1.5px solid var(--ld-outline-soft)', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
              {preview.slots.map((s, i) => (
                <label key={s.reihenfolge} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', cursor: 'pointer', borderBottom: i < preview.slots.length - 1 ? '1px solid var(--ld-border-light)' : 'none' }}>
                  <input type="checkbox" checked={sel.has(s.reihenfolge)} onChange={() => setSel(prev => { const n = new Set(prev); if (n.has(s.reihenfolge)) n.delete(s.reihenfolge); else n.add(s.reihenfolge); return n; })} style={{ width: 17, height: 17, accentColor: 'var(--ld-primary)' }} />
                  <span style={{ fontSize: 14, color: 'var(--ld-text-primary)' }}>{fmtDatum(s.datum)} · {s.start_zeit}–{s.end_zeit}</span>
                </label>
              ))}
            </div>

            {/* Mail-Vorschau (editierbar) */}
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ld-text-secondary)', marginBottom: 6 }}>Mail-Vorschau</div>
            <div style={{ fontSize: 12, color: 'var(--ld-text-secondary)', marginBottom: 6 }}>Betreff: {subject}</div>
            <textarea value={body} onChange={e => { setBody(e.target.value); setManuell(true); }} rows={9} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit', marginBottom: 18 }} />

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={onClose} style={{ flex: 1, padding: '12px 0', borderRadius: 12, border: '1.5px solid var(--ld-outline-soft)', background: 'var(--ld-bg-input)', fontSize: 15, fontWeight: 600, cursor: 'pointer', color: 'var(--ld-text-secondary)' }}>Abbrechen</button>
              <button onClick={absenden} disabled={senden || selSlots.length === 0} style={{ flex: 2, padding: '12px 0', borderRadius: 12, border: 'none', background: senden || selSlots.length === 0 ? 'var(--ld-primary-soft-strong)' : 'var(--ld-primary)', color: 'white', fontSize: 15, fontWeight: 700, cursor: senden || selSlots.length === 0 ? 'default' : 'pointer' }}>
                {senden ? 'Senden …' : `Senden (${selSlots.length})`}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </LdSheet>
  );
}
